import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, Users, Heart, Award, Calendar, Building2, Maximize2, X } from 'lucide-react'

const AboutUs = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null)

  const timeline = [
    {
      era: 'Late 1970s',
      title: 'Club Foundation',
      description: 'Lockridge Forest Swim Tennis Club was established, beginning a legacy of community recreation and family fun.',
      icon: 'clock'
    },
    {
      era: '1992-2000',
      title: 'Community Events Era',
      description: 'The club became known for memorable social gatherings including Pig Roasts, Progressive Dinners, Halloween parties, and Easter Egg Hunts that brought families together.',
      icon: 'users'
    },
    {
      era: '1995',
      title: 'Pavilion Construction',
      description: 'A major milestone in club history with the construction of the pavilion, expanding our ability to host member events and gatherings.',
      icon: 'award'
    },
    {
      era: 'April 1998',
      title: 'Tornado Recovery',
      description: 'The club demonstrated resilience and community spirit by recovering from tornado damage and making necessary repairs, emerging stronger than before.',
      icon: 'heart'
    },
    {
      era: '2000s-2010s',
      title: 'Tradition Continues',
      description: 'The club continued to grow and thrive, hosting Wine Tastings, Santa & Pony Rides, and maintaining cherished traditions while adapting to modern needs.',
      icon: 'calendar'
    },
    {
      era: 'Present Day',
      title: 'Thriving Community',
      description: 'Today, Lockridge Forest continues to be a cornerstone of the community, offering premier facilities and fostering lifelong friendships in our beautiful 9-acre setting.',
      icon: 'award'
    }
  ]

  const getIcon = (iconName) => {
    const icons = {
      clock: Clock,
      users: Users,
      heart: Heart,
      award: Award,
      calendar: Calendar,
      building: Building2
    }
    const Icon = icons[iconName] || Clock
    return <Icon className="w-6 h-6" />
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative text-white min-h-[450px] flex items-center"
        style={{backgroundImage: 'url(/images/club/image-5c5fe817.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-tertiary-800/70 to-primary-900/80"></div>
        <div className="container-custom relative z-10 py-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 animate-fade-in text-4xl md:text-5xl lg:text-6xl font-bold">
              About Us
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-95">
              Over 45 years of building community, creating memories, and fostering friendships
              at Lockridge Forest Swim Tennis Club
            </p>
          </div>
        </div>
      </section>

      {/* Founding Section */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-6">
              <Clock className="w-8 h-8 text-primary-600 mr-3" />
              <h2 className="text-gray-900">Founding</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start mb-8">
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  In the late 1970s, a vision took shape in the heart of Peachtree Corners. Lockridge Forest Swim Tennis Club
                  was established as a private recreational facility designed to bring neighbors together and provide families
                  with a beautiful 9-acre oasis for relaxation, exercise, and community connection.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  From its earliest days, the club embodied the spirit of community. Founding members worked together to create
                  not just facilities, but a gathering place where friendships would flourish and traditions would be born. The
                  original pool and tennis courts became the backdrop for countless summer days and the foundation of what would
                  become a cherished neighborhood institution.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Those early years set the tone for everything that followed—a commitment to quality facilities, family-friendly
                  atmosphere, and the understanding that a club is only as strong as the community it serves.
                </p>
              </div>

              <div className="space-y-4">
                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_1 - The Club Beginning late 1970.jpg', caption: 'The Club Beginning - Late 1970s'})}
                >
                  <img
                    src="/images/start_1 - The Club Beginning late 1970.jpg"
                    alt="The Club Beginning - Late 1970s"
                    className="w-full h-64 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">The Club Beginning - Late 1970s</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_5 - The Club Beginning late 1970.jpg', caption: 'Early Days at Lockridge Forest'})}
                >
                  <img
                    src="/images/start_5 - The Club Beginning late 1970.jpg"
                    alt="Early Days at Lockridge Forest"
                    className="w-full h-64 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Early Days at Lockridge Forest</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Timeline */}
      <section className="py-10 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-gray-900">Our Journey Through Time</h2>
            <p className="text-lg text-gray-600">
              Key milestones in the history of Lockridge Forest
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="relative pb-8 last:pb-0">
                {/* Timeline line */}
                {index !== timeline.length - 1 && (
                  <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-tertiary-500"></div>
                )}

                <div className="flex items-start">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-tertiary-500 text-white flex items-center justify-center shadow-lg z-10">
                    {getIcon(item.icon)}
                  </div>

                  {/* Content */}
                  <div className="ml-6 flex-1">
                    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm font-semibold text-primary-600 mb-1">{item.era}</div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Milestones Section */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-6">
              <Award className="w-8 h-8 text-tertiary-600 mr-3" />
              <h2 className="text-gray-900">Key Milestones</h2>
            </div>

            {/* 1990s - Community Events Era */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">1990s: The Social Heart of the Community</h3>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_9 - 1992 Aloha Pig Roast.jpg', caption: '1992 Aloha Pig Roast'})}
                >
                  <img
                    src="/images/start_9 - 1992 Aloha Pig Roast.jpg"
                    alt="1992 Aloha Pig Roast"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">1992 Aloha Pig Roast</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_31 - Summer Picnic 1994.jpg', caption: 'Summer Picnic 1994'})}
                >
                  <img
                    src="/images/start_31 - Summer Picnic 1994.jpg"
                    alt="Summer Picnic 1994"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Summer Picnic 1994</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_96 - Wine Tasting 1998.jpg', caption: 'Wine Tasting 1998'})}
                >
                  <img
                    src="/images/start_96 - Wine Tasting 1998.jpg"
                    alt="Wine Tasting 1998"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Wine Tasting 1998</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                The 1990s marked a golden era of social gatherings at Lockridge Forest. From the legendary Aloha Pig Roasts
                to festive Octoberfests, Progressive Dinners, and Wine Tastings, the club became known throughout the community
                as the place where neighbors became friends and friends became family. These weren't just events—they were
                traditions that defined the club's character and brought joy to countless families.
              </p>
            </div>

            {/* 1995 - Pavilion Construction */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">1995: Building for the Future</h3>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_41 - Pavilion Construction Summer 1995.jpg', caption: 'Pavilion Construction Begins - Summer 1995'})}
                >
                  <img
                    src="/images/start_41 - Pavilion Construction Summer 1995.jpg"
                    alt="Pavilion Construction Summer 1995"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Pavilion Construction Begins</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_48 - Pavilion Construction Summer 1995.jpg', caption: 'Pavilion Construction - Summer 1995'})}
                >
                  <img
                    src="/images/start_48 - Pavilion Construction Summer 1995.jpg"
                    alt="Pavilion Roof Construction"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Pavilion Progress</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_49 - Pavilion Construction Summer 1995.jpg', caption: 'Community Effort - Summer 1995'})}
                >
                  <img
                    src="/images/start_49 - Pavilion Construction Summer 1995.jpg"
                    alt="Pavilion Construction - Community Effort"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Community Effort</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                The summer of 1995 brought one of the most significant improvements in club history: the construction of our
                beloved pavilion. This wasn't just about adding a structure—it was about creating a covered gathering space
                that would shelter generations of celebrations, provide shade on hot summer days, and serve as the centerpiece
                for countless memories. The pavilion expanded our capacity to host events and became an integral part of the
                Lockridge Forest experience.
              </p>
            </div>

            {/* 1998 - Tornado Recovery */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">1998: Resilience in the Face of Adversity</h3>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_67 - Taj - April 1998 Tornado.jpg', caption: 'Taj - April 1998 Tornado Damage'})}
                >
                  <img
                    src="/images/start_67 - Taj - April 1998 Tornado.jpg"
                    alt="Taj Tornado Damage"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Taj - Tornado Damage</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_73 - Taj upgrade - Post April 1998 Tornado.jpg', caption: 'Taj Reconstruction'})}
                >
                  <img
                    src="/images/start_73 - Taj upgrade - Post April 1998 Tornado.jpg"
                    alt="Taj Upgrade Post-Tornado"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Taj Reconstruction</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_82 - We got the t-shirts - April 1998 Tornado.jpg', caption: 'Community Spirit Prevails'})}
                >
                  <img
                    src="/images/start_82 - We got the t-shirts - April 1998 Tornado.jpg"
                    alt="Recovery Spirit"
                    className="w-full h-48 object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-sm text-gray-700 font-medium">Community Spirit Prevails</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                April 1998 tested the strength of our community like never before. A devastating tornado swept through Lockridge,
                causing significant damage to the Taj, tennis courts, and surrounding areas. Thousands of trees were destroyed,
                and the club faced its greatest challenge. But what could have been a breaking point became a defining moment.
                Members rallied together, repairs were made, and improvements were implemented. The club emerged not just restored,
                but stronger—a testament to the resilience and dedication of the Lockridge Forest family.
              </p>
            </div>

            {/* 2000s-2010s - Continued Growth */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">2000s-2010s: Honoring Traditions, Embracing Progress</h3>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_100 - Santa & Pony Rides 1998.jpg', caption: 'Santa & Pony Rides - A Holiday Tradition'})}
                >
                  <img
                    src="/images/start_100 - Santa & Pony Rides 1998.jpg"
                    alt="Santa & Pony Rides"
                    className="w-full h-40 object-cover"
                  />
                  <div className="bg-gray-100 p-2">
                    <p className="text-xs text-gray-700 font-medium">Santa & Pony Rides</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_256 - Halloween Circa 2003.jpg', caption: 'Halloween Celebrations'})}
                >
                  <img
                    src="/images/start_256 - Halloween Circa 2003.jpg"
                    alt="Halloween Celebration"
                    className="w-full h-40 object-cover"
                  />
                  <div className="bg-gray-100 p-2">
                    <p className="text-xs text-gray-700 font-medium">Halloween Celebrations</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_311 - Tennis Deck Build April 2012.jpg', caption: 'Tennis Deck Build 2012'})}
                >
                  <img
                    src="/images/start_311 - Tennis Deck Build April 2012.jpg"
                    alt="Tennis Deck Build"
                    className="w-full h-40 object-cover"
                  />
                  <div className="bg-gray-100 p-2">
                    <p className="text-xs text-gray-700 font-medium">Tennis Deck Build 2012</p>
                  </div>
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/start_336 - 2014 Pig Roast.jpg', caption: '2014 Pig Roast'})}
                >
                  <img
                    src="/images/start_336 - 2014 Pig Roast.jpg"
                    alt="2014 Pig Roast"
                    className="w-full h-40 object-cover"
                  />
                  <div className="bg-gray-100 p-2">
                    <p className="text-xs text-gray-700 font-medium">2014 Pig Roast</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                As we entered the new millennium, Lockridge Forest continued to evolve while maintaining the traditions that
                made it special. Annual events like Santa & Pony Rides, Easter Egg Hunts, and Halloween parades became cherished
                rituals. The tennis program flourished with the Davis Cup Memorial Tournament honoring longtime members. Facility
                improvements including the tennis deck in 2012 showed our commitment to maintaining premier amenities. Through it
                all, the club remained what it had always been: a place where families gather, children grow, and friendships last
                a lifetime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Club Today */}
      <section className="py-10 bg-gradient-to-br from-primary-50 to-tertiary-50">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-6">
              <Building2 className="w-8 h-8 text-primary-600 mr-3" />
              <h2 className="text-gray-900">Our Club Today</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start mb-8">
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Today, Lockridge Forest Swim Tennis Club stands as a testament to the vision of those who founded it and the
                  dedication of the members who have sustained it through the decades. Our beautiful 9-acre property features a
                  pristine swimming pool, well-maintained tennis courts, our beloved pavilion, and the iconic Taj—all set among
                  mature trees and landscaping that create a true neighborhood oasis.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  We continue to be more than just a recreational facility. We are a community hub where three generations of
                  families have learned to swim, played tennis, celebrated birthdays, and built friendships. Children who grew up
                  swimming in our pool now bring their own children to experience the same joy. The traditions continue, adapted
                  for modern times but rooted in the same values of community, family, and friendship.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  As we look to the future, we remain committed to preserving what makes Lockridge Forest special while continuing
                  to evolve to serve new generations of members. Our story isn't just about facilities and events—it's about the
                  people who make this place a true community. We invite you to become part of our continuing story.
                </p>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">By the Numbers</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-600">45+</div>
                      <div className="text-sm text-gray-600">Years of History</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-600">9</div>
                      <div className="text-sm text-gray-600">Acres of Beauty</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-600">1000s</div>
                      <div className="text-sm text-gray-600">Families Served</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-600">505</div>
                      <div className="text-sm text-gray-600">Historical Photos</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/LFST_5 - 2025 Fall Festival.jpg', caption: '2025 Fall Festival'})}
                >
                  <img
                    src="/images/LFST_5 - 2025 Fall Festival.jpg"
                    alt="2025 Fall Festival"
                    className="w-full h-64 object-cover"
                  />
                </div>

                <div
                  className="rounded-lg overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => setSelectedPhoto({src: '/images/LFST_7 - 2025 4th of July.jpg', caption: '2025 4th of July'})}
                >
                  <img
                    src="/images/LFST_7 - 2025 4th of July.jpg"
                    alt="2025 4th of July"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Photo Archive Link */}
            <div className="text-center mt-10 p-8 bg-white rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Explore Our Complete Photo Archive</h3>
              <p className="text-lg text-gray-700 mb-6">
                Dive into over 500 historical photos documenting our club's journey from the late 1970s through 2016
              </p>
              <Link
                to="/photo-gallery"
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg"
              >
                <Maximize2 className="w-5 h-5" />
                View Photo Gallery
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPhoto.src}
              alt={selectedPhoto.caption}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
            {selectedPhoto.caption && (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg mt-4 p-4">
                <p className="text-white text-center text-lg font-medium">{selectedPhoto.caption}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Call to Action */}
      <section className="py-10 bg-gradient-to-r from-primary-600 via-tertiary-600 to-primary-800 text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6">Be Part of Our Story</h2>
            <p className="text-xl mb-8 opacity-95">
              Join Lockridge Forest Swim Tennis Club and become part of our continuing legacy.
              Create your own memories and traditions with us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/membership" className="btn btn-primary bg-white text-primary-700 hover:bg-gray-100">
                Join Our Community
              </Link>
              <Link to="/events" className="btn btn-outline border-white text-white hover:bg-white hover:text-primary-700">
                Upcoming Events
              </Link>
            </div>
            <div className="mt-8 text-sm opacity-90">
              <p>Have historical photos or stories to share?</p>
              <a href="mailto:admin@lockridgeforestswimtennis.com" className="hover:underline text-white font-medium">
                Contact us to contribute to our archive
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default AboutUs
